import _ from "lodash"
import { api } from "aldoo-ra/API"

const storeAPI = api("store")

/**
 * Validates that a productId is unique across all platform products
 * @param {string} productId - The product ID to validate
 * @param {string} platform - The platform (iOS, Android, etc.)
 * @param {object} options - Validation options
 * @param {string} options.excludePriceId - Current price ID to exclude from search
 * @param {Array} options.currentProductIds - Current editing state (array of {platform, id} objects)
 * @returns {Promise<{isValid: boolean, duplicateProduct?: object, error?: string}>}
 */
export const validateProductIdUniqueness = async (productId, platform, options = {}) => {
  try {
    // Don't validate empty values
    if (!productId || !productId.trim()) {
      return { isValid: true }
    }

    const trimmedProductId = productId.trim()

    // Extract platform string if it's an object (from dropdown selection)
    const platformString = typeof platform === 'object' && platform.value ? platform.value : platform

    // Use the simplified endpoint for platform product ID validation
    const payload = {
      "platformId": trimmedProductId,
      "platform": platformString
    }

    // Add price exclusion parameter if provided
    if (options.excludePriceId) {
      payload.excludePriceId = options.excludePriceId
    }

    // Add current editing state if provided
    if (options.currentProductIds && Array.isArray(options.currentProductIds)) {
      payload.currentProductIds = options.currentProductIds
    }

    const response = await storeAPI({ "platform-product-id-used": payload })

    if (response.error) {
      console.error('❌ API Error during validation:', response.error)
      return {
        isValid: true, // Don't block on API errors
        error: `Validation check failed: ${response.error}`
      }
    }

    // Parse the response - "used": true means conflict, "used": false means safe
    const { used, products, internalDuplicate } = response.data || response

    if (used) {
      return {
        isValid: false,
        // For backward compatibility with existing UI
        duplicateProduct: {
          price_id: internalDuplicate ? 'same product' : 'another product',
          id: trimmedProductId
        },
        // List of product PIDs using this platform ID
        conflictingProducts: products || [],
        // Flag indicating if duplicate is within the same product
        internalDuplicate: internalDuplicate || false
      }
    }

    return { isValid: true }

  } catch (error) {
    console.error('💥 ProductId validation error:', error)
    return { 
      isValid: true, // Don't block on validation errors
      error: `Validation error: ${error.message}` 
    }
  }
}

/**
 * Debounced version of the productId validator
 * @param {number} delay - Debounce delay in milliseconds (default: 500)
 */
export const createDebouncedProductIdValidator = (delay = 500) => {
  return _.debounce(validateProductIdUniqueness, delay)
}